import { useState, useEffect } from 'react';
import { dateTimeToSGTIso } from '../utils/dateUtils';
import '../assets/EditLessonModal.css';

const EditLessonModal = ({ lesson, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    date: '',
    duration: '',
    summary: '',
    tutorRate: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (lesson && isOpen) {
      // Build the datetime-local value from the stored UTC date pinned to SGT,
      // so the input shows the Singapore date/time (not the browser's local
      // interpretation, which shifts the day on a non-SGT browser).
      let sgtDateTimeString = '';
      if (lesson.date) {
        const date = new Date(lesson.date);
        const datePart = date.toLocaleDateString('en-CA', { timeZone: 'Asia/Singapore' });
        const timePart = date.toLocaleTimeString('en-GB', {
          timeZone: 'Asia/Singapore',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        sgtDateTimeString = `${datePart}T${timePart}`;
      }
      
      setFormData({
        date: sgtDateTimeString,
        duration: lesson.duration || '',
        summary: lesson.summary || '',
        tutorRate: lesson.tutorRate || ''
      });
      setError('');
    }
  }, [lesson, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!formData.date || !formData.duration) {
        throw new Error('Date and duration are required');
      }

      if (formData.duration <= 0) {
        throw new Error('Duration must be greater than 0');
      }

      if (formData.tutorRate && formData.tutorRate < 0) {
        throw new Error('Tutor rate cannot be negative');
      }

      // The datetime-local value 'YYYY-MM-DDTHH:MM' carries no timezone, so a
      // bare new Date() parse would treat it as the browser's local time.
      // Split it and interpret the picked value as Singapore time instead.
      const [datePart, timePart] = formData.date.split('T');

      // Prepare update data
      const updateData = {
        date: dateTimeToSGTIso(datePart, timePart),
        duration: parseInt(formData.duration),
        summary: formData.summary.trim()
      };

      // Only include tutorRate if it's provided and different
      if (formData.tutorRate !== '' && formData.tutorRate !== lesson.tutorRate) {
        updateData.tutorRate = parseFloat(formData.tutorRate);
      }

      await onSave(lesson._id, updateData);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update lesson');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-SG', {
      timeZone: 'Asia/Singapore',
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  if (!isOpen || !lesson) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content edit-lesson-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Lesson</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="lesson-info">
          <div className="info-row">
            <span className="label">Tutor:</span>
            <span className="value">{lesson.tutor?.name || 'N/A'}</span>
          </div>
          <div className="info-row">
            <span className="label">Tutee:</span>
            <span className="value">{lesson.tutee?.name || 'N/A'}</span>
          </div>
          <div className="info-row">
            <span className="label">Subject:</span>
            <span className="value">{lesson.subject}</span>
          </div>
          <div className="info-row">
            <span className="label">Tutee Rate:</span>
            <span className="value">${lesson.rate}/hr</span>
          </div>
          <div className="info-row">
            <span className="label">Original Submission:</span>
            <span className="value">{formatDate(lesson.createdAt)}</span>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="edit-form">
          <div className="form-group">
            <label htmlFor="date">Lesson Date & Time *</label>
            <input
              type="datetime-local"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="duration">Duration (minutes) *</label>
            <input
              type="number"
              id="duration"
              name="duration"
              value={formData.duration}
              onChange={handleChange}
              min="1"
              max="480"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="tutorRate">Tutor Rate ($/hr)</label>
            <input
              type="number"
              id="tutorRate"
              name="tutorRate"
              value={formData.tutorRate}
              onChange={handleChange}
              min="0"
              step="0.01"
              placeholder="Leave empty to keep current rate"
            />
            <small className="form-help">
              Current: ${lesson.tutorRate || 'Not set'}/hr
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="summary">Lesson Summary</label>
            <textarea
              id="summary"
              name="summary"
              value={formData.summary}
              onChange={handleChange}
              rows="4"
              placeholder="Optional lesson summary..."
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" disabled={loading} className="save-btn">
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        <div className="modal-footer">
          <small className="disclaimer">
            Note: Tutor, tutee, subject, and tutee rate cannot be changed after lesson creation.
            Lessons older than 7 days cannot be edited.
          </small>
        </div>
      </div>
    </div>
  );
};

export default EditLessonModal;